import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { api } from '../../services/api';
import { parcelDaySelectionApi, ParcelDayInfo } from '../../services/parcel-day-selection';
import { formatCalendarDate } from '../../utils/dateTime';

interface Product {
  id: number;
  name: string;
  price: number;
  quantity: number;
}

interface Bundle {
  id: number;
  name: string;
  description: string;
  price: number;
  total_value: number;
  products: Product[];
}

interface FoodParcel {
  id: number;
  name: string;
  description: string;
  price: string | number;
  image: string;
  bundles: Bundle[];
}

interface FoodParcelSelectorProps {
  onNext: () => void;
  onSelect: (data: FoodParcel) => void;
  onParcelDayAssigned?: (parcelDay: ParcelDayInfo) => void;
  onCancel?: () => void;
  cancelling?: boolean;
}

const FoodParcelSelector: React.FC<FoodParcelSelectorProps> = ({ onNext, onSelect, onParcelDayAssigned, onCancel, cancelling }) => {
  const [parcels, setParcels] = useState<FoodParcel[]>([]);
  const [selectedParcel, setSelectedParcel] = useState<FoodParcel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canPlaceOrders, setCanPlaceOrders] = useState(true);
  const [nextAvailableDay, setNextAvailableDay] = useState<ParcelDayInfo | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch food parcels via app API client (same as FoodParcelsPage)
        const parcelsResponse = await api.get('/v1/food-parcels');
        const parcelsData = parcelsResponse.data?.data ?? [];
        const parcelsWithNumericPrices = (Array.isArray(parcelsData) ? parcelsData : []).map((parcel: FoodParcel) => ({
          ...parcel,
          price: typeof parcel.price === 'string' ? parseFloat(parcel.price) : parcel.price
        }));
        setParcels(parcelsWithNumericPrices);

        // Check if orders can be placed and get next available day
        const canPlace = await parcelDaySelectionApi.canPlaceOrders();
        setCanPlaceOrders(canPlace);

        if (canPlace) {
          const nextDay = await parcelDaySelectionApi.getNextAvailableDay();
          setNextAvailableDay(nextDay);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSelect = (parcel: FoodParcel) => {
    if (!canPlaceOrders) {
      return; // Don't allow selection when orders are closed
    }
    setSelectedParcel(parcel);
    onSelect(parcel);
  };

  const handleNext = async () => {
    if (selectedParcel && canPlaceOrders) {
      // Automatically assign the next available parcel day
      const assignedParcelDay = await parcelDaySelectionApi.assignNextAvailableDay();
      
      // Call the onSelect with the selected parcel
      onSelect(selectedParcel);
      
      // Call the onParcelDayAssigned callback if provided
      if (assignedParcelDay && onParcelDayAssigned) {
        onParcelDayAssigned(assignedParcelDay);
      }
      
      onNext();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {!canPlaceOrders && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>No orders can be placed at this time.</strong> Please check the schedule for when ordering will be available.
          </Typography>
        </Alert>
      )}

      {nextAvailableDay && canPlaceOrders && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Next Available Pickup:</strong> {formatCalendarDate(nextAvailableDay.date)} ({nextAvailableDay.status_text})
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {parcels.map((parcel) => (
          <Grid item xs={12} sm={6} md={4} key={parcel.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                border: selectedParcel?.id === parcel.id ? '2px solid #1976d2' : 'none',
              }}
              onClick={() => handleSelect(parcel)}
            >
              <CardMedia
                component="img"
                height="200"
                image={parcel.image}
                alt={parcel.name}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h5" component="h2">
                  {parcel.name}
                </Typography>
                <Typography>{parcel.description}</Typography>
                <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
                  ${Number(parcel.price).toFixed(2)}
                </Typography>

                {parcel.bundles && parcel.bundles.length > 0 && (
                  <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocalOfferIcon />
                        <Typography>Included Bundles</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List>
                        {parcel.bundles.map((bundle) => (
                          <ListItem key={bundle.id}>
                            <ListItemText
                              primary={bundle.name}
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {bundle.description}
                                  </Typography>
                                  <Box display="flex" gap={1} mt={1}>
                                    <Chip
                                      size="small"
                                      label={`Value: $${bundle.total_value.toFixed(2)}`}
                                      color="primary"
                                      variant="outlined"
                                    />
                                    <Chip
                                      size="small"
                                      label={`Price: $${bundle.price.toFixed(2)}`}
                                      color="secondary"
                                      variant="outlined"
                                    />
                                  </Box>
                                  <List dense>
                                    {bundle.products.map((product) => (
                                      <ListItem key={product.id}>
                                        <ListItemText
                                          primary={product.name}
                                          secondary={`Quantity: ${product.quantity}`}
                                        />
                                        <Typography variant="body2">
                                          ${product.price.toFixed(2)}
                                        </Typography>
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
        <Box>
          {onCancel && (
            <Button variant="outlined" color="error" onClick={onCancel} disabled={cancelling}>
              {cancelling ? 'Cancelling...' : 'Cancel Order'}
            </Button>
          )}
        </Box>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleNext}
          disabled={!selectedParcel || !canPlaceOrders}
        >
          {canPlaceOrders ? 'Continue to Order Details' : 'Orders Currently Closed'}
        </Button>
      </Box>
    </Box>
  );
};

export default FoodParcelSelector; 